import React from "react";
import PlacesAutocomplete from "../PlacesAutocomplete/PlacesAutocomplete.tsx";
import CustomSelect from "../../../pages/ProjectDocumentation/components/CustomSelect.tsx";
import { JURISDICTIONS } from "../../../constants/jurisdictions.ts";
import { mapGoogleProvinceToJurisdiction } from "../../../utils/jurisdictionFromPlace.ts";
import type { GeocoderAddressComponent } from "../PlacesAutocomplete/PlacesAutocomplete.tsx";

interface LocationWithJurisdictionProps {
  addressValue: string;
  addressOnChange: (value: string) => void;
  jurisdictionValue: string;
  jurisdictionOnChange: (value: string) => void;
  onPlaceSelect?: (
    address: string,
    jurisdictionId: number | null,
    coords?: { lat: number; lng: number } | null
  ) => void;
  placeholder?: string;
  jurisdictionPlaceholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  hidePlacesLabel?: boolean;
  placesInputAriaLabel?: string;
}

const LocationWithJurisdiction: React.FC<LocationWithJurisdictionProps> = ({
  addressValue,
  addressOnChange,
  jurisdictionValue,
  jurisdictionOnChange,
  onPlaceSelect,
  placeholder = "Ej: Pilar, Palermo, Córdoba",
  jurisdictionPlaceholder = "Seleccionar provincia",
  className = "",
  error,
  disabled = false,
  hidePlacesLabel = false,
  placesInputAriaLabel,
}) => {
  const jurisdictionOptions = JURISDICTIONS.map((j) => ({
    value: j.id.toString(),
    label: j.name,
  }));

  const showJurisdictionFallback = !jurisdictionValue && addressValue.length > 0;

  const handlePlaceSelect = (place: {
    address: string;
    lat: number;
    lng: number;
    addressComponents?: GeocoderAddressComponent[];
  }) => {
    const jurisdictionId = mapGoogleProvinceToJurisdiction(
      place.addressComponents,
      JURISDICTIONS
    );
    if (jurisdictionId !== null) {
      jurisdictionOnChange(jurisdictionId.toString());
    }
    onPlaceSelect?.(place.address, jurisdictionId, { lat: place.lat, lng: place.lng });
  };

  const handleAddressChange = (value: string) => {
    addressOnChange(value);
    if (!value) {
      jurisdictionOnChange("");
    }
  };

  return (
    <div className={className}>
      <PlacesAutocomplete
        value={addressValue}
        onChange={handleAddressChange}
        onPlaceSelect={handlePlaceSelect}
        placeholder={placeholder}
        error={error}
        disabled={disabled}
        hideLabel={hidePlacesLabel}
        inputAriaLabel={placesInputAriaLabel}
      />
      {showJurisdictionFallback && (
        <div style={{ marginTop: "0.8rem" }}>
          <label
            htmlFor="jurisdiction-fallback"
            style={{
              display: "block",
              fontWeight: 600,
              fontSize: "1.4rem",
              color: "var(--text-title, #0f172a)",
              marginBottom: "0.8rem",
            }}
          >
            Provincia
          </label>
          <CustomSelect
            value={jurisdictionValue}
            onChange={jurisdictionOnChange}
            options={jurisdictionOptions}
            placeholder={jurisdictionPlaceholder}
          />
          <span
            style={{
              display: "block",
              marginTop: "0.4rem",
              fontSize: "1.2rem",
              color: "var(--text-muted, #64748b)",
            }}
          >
            No identificamos la provincia. Seleccioná manualmente.
          </span>
        </div>
      )}
    </div>
  );
};

export default LocationWithJurisdiction;
